import { memo, useCallback, useMemo } from 'react';
import type { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { NewsTypeEnum } from 'APIPackage/NewsModule/enums/NewsTypeEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';

import { mapNewsType } from '../common/mapNewsType';
import styles from './Modal.module.scss';
import { INewsFile } from '../interfaces/INewsFile';
import { INewsForm } from '../interfaces/INewsForm';
import { newsFormAdd, newsFormUpdate } from '../actions/newsFormActions';

interface INewsConfirmModalProps {
  visible: boolean;
  values: INewsForm;
  files: INewsFile[];
  deletedFiles: number[];
  newsId?: string;
  onClose: () => void;
}

export const ConfirmNewsModal = memo(
  ({ newsId, values, visible, files, onClose, deletedFiles }: INewsConfirmModalProps) => {
    const dispatch = useDispatch<Dispatch<RootActions>>();
    const isWithoutPublishDate = Boolean(values.published);
    const history = useHistory();

    const update = useCallback(() => {
      if (newsId) {
        dispatch(newsFormUpdate(values, deletedFiles, files));
      }
    }, [deletedFiles, dispatch, files, newsId, values]);

    const onConfirmModal = useCallback(() => {
      update();
      onClose();
    }, [onClose, update]);

    const onPublish = useCallback(() => {
      if (newsId) {
        dispatch(newsFormUpdate({ ...values, published: new Date().toLocaleDateString() }));
      } else {
        dispatch(newsFormAdd({ ...values, published: new Date().toLocaleDateString() }, history));
      }
      onClose();
    }, [dispatch, history, newsId, onClose, values]);

    const onAdd = () => {
      dispatch(newsFormAdd(values, history));
    };

    const valueIsPublishedText: string = useMemo(() => {
      return values.type?.id === NewsTypeEnum.NEWS ? 'Новость уже опубликована' : 'Анонс уже опубликован';
    }, [values.type?.id]);

    const newsBody: string = useMemo(() => {
      return isWithoutPublishDate
        ? `${valueIsPublishedText}. Желаете сохранить изменения?`
        : `Вы хотите опубликовать ${values.type?.id && mapNewsType(values.type.id)} или сохранить без публикации?`;
    }, [isWithoutPublishDate, valueIsPublishedText, values.type?.id]);

    return (
      <Modal visible={visible} modalBodyClassName={styles.Modal} onClose={onClose}>
        <ModalHeader title={values.type?.id && mapNewsType(values.type.id)} />
        <ModalBody size="content" border="bottom">
          {newsBody}
        </ModalBody>
        <ModalActions>
          {isWithoutPublishDate ? (
            <Button color="primary" size="medium" id="confirmButton" text="Применить" onClick={onConfirmModal} />
          ) : (
            <>
              <Button
                color="primary"
                size="medium"
                text="Сохранить без публикации"
                id="saveButton"
                onClick={newsId ? onConfirmModal : onAdd}
              />
              <Button
                className="shared-btn"
                color="primary"
                size="medium"
                type="button"
                id="publishButton"
                text="Опубликовать"
                onClick={onPublish}
              />
            </>
          )}
          <Button color="default" size="medium" text="Отменить" type="button" onClick={onClose} />
        </ModalActions>
      </Modal>
    );
  },
);
